import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import {
  BoxParams,
  generateBoxGeometry,
  generateLidGeometry,
  exportBoxToSTL
} from '@/lib/boxGenerator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  Download,
  RotateCcw,
  Box,
  Layers,
  Sliders,
  Grid,
  PackageCheck,
  RotateCw,
  Crosshair
} from 'lucide-react';
import { toast } from 'sonner';

interface PresetItem {
  name: string;
  badge: string;
  description: string;
  params: BoxParams;
}

const BOX_PRESETS: PresetItem[] = [
  {
    name: 'Organizador Cajón 2x2',
    badge: 'Popular',
    description: 'Caja simétrica de 4 compartimentos con esquinas suaves',
    params: {
      width: 120,
      length: 120,
      height: 40,
      wallThickness: 2.0,
      bottomThickness: 2.4,
      cornerRadius: 6.0,
      columnsX: 2,
      rowsY: 2,
      dividerThickness: 1.4,
      dividerHeightPercent: 100,
      lidType: 'none',
      lidThickness: 2.0,
      lidClearance: 0.35,
      hasFingerNotch: false
    }
  },
  {
    name: 'Caja con Tapa Snap',
    badge: 'Con Tapa',
    description: 'Caja alargada para repuestos con tapa a presión',
    params: {
      width: 140,
      length: 80,
      height: 45,
      wallThickness: 2.0,
      bottomThickness: 2.4,
      cornerRadius: 5.0,
      columnsX: 3,
      rowsY: 1,
      dividerThickness: 1.2,
      dividerHeightPercent: 95,
      lidType: 'snap',
      lidThickness: 2.2,
      lidClearance: 0.35,
      hasFingerNotch: true
    }
  },
  {
    name: 'Bandeja Minimal',
    badge: 'Escritorio',
    description: 'Bandeja amplia sin divisiones para escritorio Japandi',
    params: {
      width: 160,
      length: 100,
      height: 25,
      wallThickness: 2.2,
      bottomThickness: 2.5,
      cornerRadius: 8.0,
      columnsX: 1,
      rowsY: 1,
      dividerThickness: 1.2,
      dividerHeightPercent: 100,
      lidType: 'none',
      lidThickness: 2.0,
      lidClearance: 0.35,
      hasFingerNotch: false
    }
  },
  {
    name: 'Joyero 3x3',
    badge: '9 Celdas',
    description: '9 compartimentos pequeños para accesorios o joyería',
    params: {
      width: 110,
      length: 110,
      height: 35,
      wallThickness: 1.8,
      bottomThickness: 2.0,
      cornerRadius: 4.0,
      columnsX: 3,
      rowsY: 3,
      dividerThickness: 1.2,
      dividerHeightPercent: 85,
      lidType: 'snap',
      lidThickness: 2.0,
      lidClearance: 0.35,
      hasFingerNotch: true
    }
  }
];

const defaultParams: BoxParams = BOX_PRESETS[0].params;

export default function BoxGeneratorPage() {
  const mountRef = useRef<HTMLDivElement>(null);
  const [params, setParams] = useState<BoxParams>(defaultParams);
  const [wireframe, setWireframe] = useState(false);
  const [boxColor, setBoxColor] = useState('#D8C7B5'); // Lino
  const [lidColor, setLidColor] = useState('#B85D3B'); // Terracota
  const [activeTab, setActiveTab] = useState<'dimensions' | 'grid' | 'lid'>('dimensions');
  const [autoRotate, setAutoRotate] = useState(false);

  // Three.js refs
  const sceneRef = useRef<THREE.Scene | null>(null);
  const boxMeshRef = useRef<THREE.Mesh | null>(null);
  const lidMeshRef = useRef<THREE.Mesh | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const boxGeomRef = useRef<THREE.BufferGeometry | null>(null);
  const lidGeomRef = useRef<THREE.BufferGeometry | null>(null);

  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.autoRotate = autoRotate;
    }
  }, [autoRotate]);

  // Inicializar Three.js
  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    const width = container.clientWidth;
    const height = container.clientHeight || 560;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xF9F8F6);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(45, width / height, 1, 1000);
    camera.position.set(120, 160, 200);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    rendererRef.current = renderer;

    container.appendChild(renderer.domElement);

    // Luces
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.75);
    scene.add(ambientLight);

    const dirLight1 = new THREE.DirectionalLight(0xffffff, 0.85);
    dirLight1.position.set(160, 220, 160);
    scene.add(dirLight1);

    const dirLight2 = new THREE.DirectionalLight(0xE59866, 0.4);
    dirLight2.position.set(-160, 100, -100);
    scene.add(dirLight2);

    // Grilla
    const grid = new THREE.GridHelper(300, 30, 0xD4CDC5, 0xEAE4DD);
    grid.position.y = -0.5;
    scene.add(grid);

    // Geometría Caja
    const boxGeom = generateBoxGeometry(params);
    boxGeomRef.current = boxGeom;
    const boxMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(boxColor),
      roughness: 0.4,
      metalness: 0.05,
      side: THREE.DoubleSide,
      wireframe
    });
    const boxMesh = new THREE.Mesh(boxGeom, boxMat);
    boxMeshRef.current = boxMesh;
    scene.add(boxMesh);

    // Geometría Tapa
    if (params.lidType !== 'none') {
      const lidGeom = generateLidGeometry(params);
      lidGeomRef.current = lidGeom;
      const lidMat = new THREE.MeshStandardMaterial({
        color: new THREE.Color(lidColor),
        roughness: 0.4,
        metalness: 0.05,
        side: THREE.DoubleSide,
        wireframe
      });
      const lidMesh = new THREE.Mesh(lidGeom, lidMat);
      lidMeshRef.current = lidMesh;
      scene.add(lidMesh);
    }

    // Controles orbitales de cámara
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.target.set(0, params.height * 0.4, 0);
    controls.autoRotate = autoRotate;
    controls.autoRotateSpeed = 2.0;
    controls.maxDistance = 500;
    controls.minDistance = 50;
    controlsRef.current = controls;

    let animId: number;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      if (!container || !rendererRef.current) return;
      const nw = container.clientWidth;
      const nh = container.clientHeight || 560;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      rendererRef.current.setSize(nw, nh);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
      controls.dispose();
      renderer.dispose();
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    };
  }, []);

  const handleRecenter = () => {
    if (!cameraRef.current || !controlsRef.current) return;
    controlsRef.current.target.set(0, params.height * 0.4, 0);
    cameraRef.current.position.set(120, 160, 200);
    controlsRef.current.update();
    toast.info('Vista centrada');
  };

  // Actualizar geometrías
  useEffect(() => {
    if (!sceneRef.current || !boxMeshRef.current) return;

    // Actualizar caja
    const newBoxGeom = generateBoxGeometry(params);
    boxGeomRef.current = newBoxGeom;
    boxMeshRef.current.geometry.dispose();
    boxMeshRef.current.geometry = newBoxGeom;

    // Actualizar tapa
    if (params.lidType !== 'none') {
      const newLidGeom = generateLidGeometry(params);
      lidGeomRef.current = newLidGeom;

      if (!lidMeshRef.current) {
        const lidMat = new THREE.MeshStandardMaterial({
          color: new THREE.Color(lidColor),
          roughness: 0.4,
          metalness: 0.05,
          side: THREE.DoubleSide,
          wireframe
        });
        const lidMesh = new THREE.Mesh(newLidGeom, lidMat);
        lidMeshRef.current = lidMesh;
        sceneRef.current.add(lidMesh);
      } else {
        lidMeshRef.current.geometry.dispose();
        lidMeshRef.current.geometry = newLidGeom;
        lidMeshRef.current.visible = true;
      }
    } else {
      if (lidMeshRef.current) {
        lidMeshRef.current.visible = false;
      }
    }
  }, [params]);

  // Actualizar materiales
  useEffect(() => {
    if (boxMeshRef.current) {
      const mat = boxMeshRef.current.material as THREE.MeshStandardMaterial;
      mat.color.set(boxColor);
      mat.wireframe = wireframe;
    }
    if (lidMeshRef.current) {
      const mat = lidMeshRef.current.material as THREE.MeshStandardMaterial;
      mat.color.set(lidColor);
      mat.wireframe = wireframe;
    }
  }, [boxColor, lidColor, wireframe]);

  const handleApplyPreset = (item: PresetItem) => {
    setParams(item.params);
    toast.success(`Preset "${item.name}" aplicado`);
  };

  const handleExportBox = () => {
    if (!boxGeomRef.current) return;
    toast.success('Descargando caja organizadora...');
    exportBoxToSTL(boxGeomRef.current, `caja-${params.width}x${params.length}x${params.height}mm.stl`);
  };

  const handleExportLid = () => {
    if (!lidGeomRef.current) return;
    toast.success('Descargando tapa encastrable...');
    exportBoxToSTL(lidGeomRef.current, `tapa-${params.width}x${params.length}mm.stl`);
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* Header */}
      <div className="space-y-3">
        <Link
          to="/tools"
          className="inline-flex items-center text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Volver al Hub de Herramientas
        </Link>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Generador de Cajas & Organizadores</h1>
              <Badge className="bg-primary/10 text-primary border-primary/20">Organización 3D</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Cajas milimétricas con esquinas redondeadas, separadores configurables y tapa a presión calibrada.
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setParams(defaultParams)}
              className="gap-1.5"
            >
              <RotateCcw className="h-4 w-4" /> Reset
            </Button>
            <Button
              size="sm"
              onClick={handleExportBox}
              className="gap-1.5 bg-primary text-primary-foreground shadow-sm"
            >
              <Download className="h-4 w-4" /> Caja STL
            </Button>
            {params.lidType !== 'none' && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleExportLid}
                className="gap-1.5 shadow-sm"
              >
                <Download className="h-4 w-4" /> Tapa STL
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Presets */}
      <div className="space-y-2">
        <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Plantillas Rápidas
        </Label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {BOX_PRESETS.map(item => {
            const isMatch = params.width === item.params.width && params.length === item.params.length && params.columnsX === item.params.columnsX;
            return (
              <button
                key={item.name}
                onClick={() => handleApplyPreset(item)}
                className={`p-3 rounded-xl border text-left transition-all flex flex-col justify-between ${
                  isMatch
                    ? 'border-primary bg-primary/5 ring-1 ring-primary shadow-xs'
                    : 'border-border/70 bg-card hover:bg-muted/40'
                }`}
              >
                <div>
                  <Badge variant="outline" className="text-[9px] px-1 py-0 mb-1 font-normal">
                    {item.badge}
                  </Badge>
                  <h4 className="font-semibold text-xs">{item.name}</h4>
                </div>
                <p className="text-[10px] text-muted-foreground line-clamp-2 mt-1">
                  {item.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Grid: 3D Viewport + Controls */}
      <div className="grid lg:grid-cols-12 gap-8 items-start">
        {/* Viewport 3D */}
        <div className="lg:col-span-7 flex flex-col gap-3">
          <Card className="overflow-hidden border border-border/80 shadow-sm relative flex flex-col min-h-[520px] lg:min-h-[620px] bg-[#F9F8F6]">
            {/* Controles flotantes */}
            <div className="absolute top-4 left-4 right-4 z-10 flex items-center justify-between pointer-events-none">
              <div className="pointer-events-auto flex gap-2">
                <Button
                  size="sm"
                  variant={autoRotate ? "default" : "secondary"}
                  className={`text-xs gap-1.5 shadow-sm h-8 ${
                    autoRotate ? 'bg-primary text-primary-foreground' : ''
                  }`}
                  onClick={() => setAutoRotate(!autoRotate)}
                  title="Giro automático 360°"
                >
                  <RotateCw className={`h-3.5 w-3.5 ${autoRotate ? 'animate-spin' : ''}`} />
                  {autoRotate ? 'Giro ON' : 'Giro Auto'}
                </Button>

                <Button
                  size="sm"
                  variant="secondary"
                  className="text-xs gap-1.5 shadow-sm h-8"
                  onClick={handleRecenter}
                  title="Centrar y reajustar vista"
                >
                  <Crosshair className="h-3.5 w-3.5" /> Centrar
                </Button>

                <Button
                  size="sm"
                  variant={wireframe ? "default" : "secondary"}
                  className="text-xs gap-1 shadow-sm h-8"
                  onClick={() => setWireframe(!wireframe)}
                >
                  <Layers className="h-3.5 w-3.5" /> Malla
                </Button>
              </div>

              <div className="pointer-events-auto flex items-center gap-1.5 bg-card/90 backdrop-blur-xs p-1 rounded-lg border border-border/60 shadow-xs">
                {[
                  { box: '#D8C7B5', lid: '#B85D3B', label: 'Lino / Terracota' },
                  { box: '#F5F2EB', lid: '#7A8B7B', label: 'Blanco / Salvia' },
                  { box: '#1C1C1E', lid: '#D8C7B5', label: 'Carbón / Lino' }
                ].map((palette, idx) => (
                  <button
                    key={idx}
                    title={palette.label}
                    className="w-5 h-5 rounded-full border border-black/10 overflow-hidden flex"
                    onClick={() => {
                      setBoxColor(palette.box);
                      setLidColor(palette.lid);
                    }}
                  >
                    <span className="w-1/2 h-full" style={{ backgroundColor: palette.box }} />
                    <span className="w-1/2 h-full" style={{ backgroundColor: palette.lid }} />
                  </button>
                ))}
              </div>
            </div>

            {/* Canvas Three.js */}
            <div ref={mountRef} className="w-full flex-1 cursor-grab active:cursor-grabbing" />

            {/* Pie informativo */}
            <div className="p-3 bg-card/80 backdrop-blur-xs border-t border-border/60 text-xs text-muted-foreground flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <Box className="h-3.5 w-3.5 text-primary" />
                Rotar: Arrastre • Zoom: Rueda
              </span>
              <div className="flex items-center gap-3">
                <span>Medidas: <strong className="text-foreground">{params.width}×{params.length}×{params.height} mm</strong></span>
                <span>Celdas: <strong className="text-foreground">{params.columnsX * params.rowsY}</strong></span>
                <span>Tapa: <strong className="text-foreground">{params.lidType === 'none' ? 'Sin Tapa' : 'Snap a Presión'}</strong></span>
              </div>
            </div>
          </Card>
        </div>

        {/* Panel Paramétrico */}
        <div className="lg:col-span-5 space-y-4">
          <Card className="border border-border/80 shadow-xs">
            <CardHeader className="pb-3 border-b border-border/50">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sliders className="h-4 w-4 text-primary" /> Controles Paramétricos
                </CardTitle>
                <Badge variant="secondary" className="text-[10px] font-mono">
                  {params.width}×{params.length}mm
                </Badge>
              </div>

              {/* Segment Tabs */}
              <div className="grid grid-cols-3 gap-1 pt-3">
                <button
                  onClick={() => setActiveTab('dimensions')}
                  className={`py-1.5 px-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 transition-all ${
                    activeTab === 'dimensions'
                      ? 'bg-primary text-primary-foreground shadow-xs'
                      : 'bg-muted/60 text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Box className="h-3.5 w-3.5" /> Dimensiones
                </button>
                <button
                  onClick={() => setActiveTab('grid')}
                  className={`py-1.5 px-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 transition-all ${
                    activeTab === 'grid'
                      ? 'bg-primary text-primary-foreground shadow-xs'
                      : 'bg-muted/60 text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Grid className="h-3.5 w-3.5" /> Divisiones
                </button>
                <button
                  onClick={() => setActiveTab('lid')}
                  className={`py-1.5 px-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 transition-all ${
                    activeTab === 'lid'
                      ? 'bg-primary text-primary-foreground shadow-xs'
                      : 'bg-muted/60 text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <PackageCheck className="h-3.5 w-3.5" /> Tapa
                </button>
              </div>
            </CardHeader>

            <CardContent className="pt-4 space-y-4">
              {/* TAB 1: DIMENSIONES */}
              {activeTab === 'dimensions' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <Label>Ancho (X)</Label>
                        <span className="text-primary font-semibold">{params.width} mm</span>
                      </div>
                      <input
                        type="range"
                        min="50"
                        max="220"
                        value={params.width}
                        onChange={e => setParams({ ...params, width: Number(e.target.value) })}
                        className="w-full accent-primary cursor-pointer"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <Label>Largo (Y)</Label>
                        <span className="text-primary font-semibold">{params.length} mm</span>
                      </div>
                      <input
                        type="range"
                        min="50"
                        max="220"
                        value={params.length}
                        onChange={e => setParams({ ...params, length: Number(e.target.value) })}
                        className="w-full accent-primary cursor-pointer"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <Label>Altura Total (Z)</Label>
                      <span className="text-primary font-semibold">{params.height} mm</span>
                    </div>
                    <input
                      type="range"
                      min="15"
                      max="120"
                      value={params.height}
                      onChange={e => setParams({ ...params, height: Number(e.target.value) })}
                      className="w-full accent-primary cursor-pointer"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border/50">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <Label>Radio de Esquinas</Label>
                        <span className="font-semibold">{params.cornerRadius} mm</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="16"
                        step="0.5"
                        value={params.cornerRadius}
                        onChange={e => setParams({ ...params, cornerRadius: Number(e.target.value) })}
                        className="w-full accent-primary cursor-pointer"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <Label>Grosor de Pared</Label>
                        <span className="font-semibold">{params.wallThickness} mm</span>
                      </div>
                      <input
                        type="range"
                        min="1.2"
                        max="3.5"
                        step="0.2"
                        value={params.wallThickness}
                        onChange={e => setParams({ ...params, wallThickness: Number(e.target.value) })}
                        className="w-full accent-primary cursor-pointer"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: DIVISIONES */}
              {activeTab === 'grid' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <Label>Columnas en X</Label>
                        <span className="text-primary font-semibold">{params.columnsX}</span>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="6"
                        value={params.columnsX}
                        onChange={e => setParams({ ...params, columnsX: Number(e.target.value) })}
                        className="w-full accent-primary cursor-pointer"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <Label>Filas en Y</Label>
                        <span className="text-primary font-semibold">{params.rowsY}</span>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="6"
                        value={params.rowsY}
                        onChange={e => setParams({ ...params, rowsY: Number(e.target.value) })}
                        className="w-full accent-primary cursor-pointer"
                      />
                    </div>
                  </div>

                  <div className="p-3 bg-muted/40 rounded-xl space-y-2 border border-border/50">
                    <div className="flex justify-between text-xs">
                      <Label>Altura de los Separadores</Label>
                      <span className="font-semibold text-primary">{params.dividerHeightPercent}%</span>
                    </div>
                    <input
                      type="range"
                      min="40"
                      max="100"
                      step="5"
                      value={params.dividerHeightPercent}
                      onChange={e => setParams({ ...params, dividerHeightPercent: Number(e.target.value) })}
                      className="w-full accent-primary cursor-pointer"
                    />
                    <p className="text-[10px] text-muted-foreground">
                      Permite dejar un margen superior para que los objetos no choquen con la tapa.
                    </p>
                  </div>
                </div>
              )}

              {/* TAB 3: TAPA */}
              {activeTab === 'lid' && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs mb-1.5 block">Configuración de Tapa</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        size="sm"
                        variant={params.lidType === 'none' ? "default" : "outline"}
                        className="text-xs h-8"
                        onClick={() => setParams({ ...params, lidType: 'none' })}
                      >
                        Sin Tapa
                      </Button>
                      <Button
                        size="sm"
                        variant={params.lidType === 'snap' ? "default" : "outline"}
                        className="text-xs h-8"
                        onClick={() => setParams({ ...params, lidType: 'snap' })}
                      >
                        Tapa a Presión (Snap)
                      </Button>
                    </div>
                  </div>

                  {params.lidType !== 'none' && (
                    <div className="space-y-3 pt-2 border-t border-border/50">
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <Label>Espesor de la Tapa</Label>
                          <span className="font-semibold text-primary">{params.lidThickness} mm</span>
                        </div>
                        <input
                          type="range"
                          min="1.4"
                          max="3.5"
                          step="0.2"
                          value={params.lidThickness}
                          onChange={e => setParams({ ...params, lidThickness: Number(e.target.value) })}
                          className="w-full accent-primary cursor-pointer"
                        />
                      </div>

                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <Label>Holgura de Encastre</Label>
                          <span className="font-semibold">{params.lidClearance} mm</span>
                        </div>
                        <input
                          type="range"
                          min="0.2"
                          max="0.8"
                          step="0.05"
                          value={params.lidClearance}
                          onChange={e => setParams({ ...params, lidClearance: Number(e.target.value) })}
                          className="w-full accent-primary cursor-pointer"
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">
                          0.35mm recomendado para boquillas de 0.4mm con ajuste suave.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
